"use client";

import { useEffect, useMemo, useRef } from "react";
import { Track } from "@/src/lib/tracking";
import { overlapAttributionParams } from "../tracking/overlapAttribution";
import { consumePendingOverlapContext } from "../utils/overlapChatContext";
import { noteOverlapResult } from "../utils/overlapAdChat";
import {
  isOverlapInsufficientCard,
  parseInsufficientEtfCount,
  parseOverlapResultMarker,
  type OverlapResultMarker,
} from "../utils/overlapResultMarker";

export interface UseOverlapMessageInput {
  messageId: string;
  /** 所在会话；用于广告返回时判断「本会话出没出过结果」 */
  chatId?: string;
  /** 已处理过的消息正文（`Message` 里的 `messageContent`） */
  content: string;
  isUserMessage: boolean;
  isToolMessage: boolean;
  isLast?: boolean;
  streamLoading?: boolean;
  /** 消息下挂的连账户卡标题；没有连账户卡传 `undefined` */
  requestPortfolioTitle?: string;
}

export type OverlapMessageOrigin = "result" | "insufficient";

/**
 * Chat 消息的重叠分析识别 + 曝光（EDG-561 `overlap_result_view` / `overlap_insufficient_view`）。
 *
 * - 结果消息：`<dynamic_ui id="etf_overlap_*">` 开标签（契约集中在 `overlapResultMarker.ts`）；
 * - ETF 不足消息：没有 dynamic_ui，靠其下连账户卡标题 `Add your other funds`；
 * - 同一 `messageId` 只发一次（`useRef`，不落盘、不新增 store）；流式输出中不发，等流结束；
 * - `entry_source` / `input_source` 取自 `consumePendingOverlapContext()`，取不到就省略该键，不造假值；
 *   `has_holdings` 由 `input_source` 派生（`existing` = 入口时已有持仓），与 `auth_signin_success.has_holdings` 同口径；
 * - 非重叠消息：`parseOverlapResultMarker` 先 `includes` 短路，没有额外解析开销。
 */
export const useOverlapMessage = ({
  messageId,
  chatId,
  content,
  isUserMessage,
  isToolMessage,
  isLast,
  streamLoading,
  requestPortfolioTitle,
}: UseOverlapMessageInput): {
  overlapMarker: OverlapResultMarker | null;
  overlapOrigin: OverlapMessageOrigin | undefined;
} => {
  const overlapMarker = useMemo(
    () =>
      isUserMessage || isToolMessage ? null : parseOverlapResultMarker(content),
    [isUserMessage, isToolMessage, content],
  );
  const overlapOrigin: OverlapMessageOrigin | undefined = overlapMarker
    ? "result"
    : isOverlapInsufficientCard(requestPortfolioTitle)
      ? "insufficient"
      : undefined;

  // 出现过结果标记就记一笔（不等流结束）：广告返回的 `overlap_chat_exit.has_result` 读它
  const hasMarker = Boolean(overlapMarker);
  useEffect(() => {
    if (hasMarker && chatId) {
      noteOverlapResult(chatId);
    }
  }, [hasMarker, chatId]);

  const exposedRef = useRef<string | null>(null);
  const done = !(streamLoading && isLast);

  useEffect(() => {
    if (!overlapOrigin || !done || !messageId) {
      return;
    }
    if (exposedRef.current === messageId) {
      return;
    }
    exposedRef.current = messageId;

    if (overlapOrigin === "result" && overlapMarker) {
      const ctx = consumePendingOverlapContext();
      Track.view("overlap_result_view", {
        ...overlapAttributionParams(),
        ...(ctx?.entry_source ? { entry_source: ctx.entry_source } : {}),
        ...(ctx?.input_source ? { input_source: ctx.input_source } : {}),
        // M-6 按「入口时是否已有持仓」分组：`existing` = 有，upload / plaid 是引导页之后才新增的输入；缺 input_source 不造值
        ...(ctx?.input_source
          ? { has_holdings: ctx.input_source === "existing" }
          : {}),
        fund_count: overlapMarker.fundCount,
        uncovered_pct: overlapMarker.uncoveredPct,
        missing_fields: overlapMarker.missingFields?.join(","),
        quota_exhausted: overlapMarker.quotaExhausted,
      });
      return;
    }
    Track.view("overlap_insufficient_view", {
      ...overlapAttributionParams(),
      etf_count: parseInsufficientEtfCount(content),
    });
  }, [overlapOrigin, overlapMarker, done, messageId, content]);

  return { overlapMarker, overlapOrigin };
};
